'use client';

import { useEffect, useReducer, useRef, useState } from 'react';
import { api, ApiRequestError } from '@/lib/api';
import {
  LAUNCH_REQUEST_TIMEOUT_MS,
  LAUNCH_TIMEOUT_MESSAGE,
  isLaunchRequestTimeout,
  launchAdvisoryNote,
} from '@/lib/launch-request';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AltitudeControl } from '@/components/dashboard/AltitudeControl';
import { TONE_TEXT } from '@/lib/dock-status';
import type { AltitudeProfile, AltitudeUnit } from '@/lib/altitude';
import { Crosshair } from 'lucide-react';

/**
 * One selectable aircraft.
 *
 * Deliberately NOT `LiveDrone`: the list is no longer "what the telemetry
 * stream is carrying". A drone stationed in a bay is powered OFF until the
 * launch sequence sends `pre_takeoff`, so it publishes nothing and can never
 * appear in the live map - and it is exactly the aircraft an operator reaches
 * for. Typing the prop to the telemetry shape is what made that unrepresentable.
 */
export interface DispatchDroneOption {
  id: string;
  /** Uppercased callsign, falling back to the name then the id tail. */
  label: string;
  /**
   * The bay it is stationed in, or null when it is not in one.
   *
   * `{ name: null }` is "in a bay we cannot name yet" - the dock registry is its
   * own fetch. Deliberately NOT collapsed into null: that is the difference
   * between "no launch sequence is coming" and "one is, from a dock still
   * loading", and the copy below says only what it knows.
   *
   * This is ASSIGNMENT (`drones.dock_id`), not presence. The dock's own
   * `drone_on_hub` byte is the presence signal and the two are known to
   * disagree, so nothing here claims the aircraft is physically on the pad.
   */
  dock: { name: string | null } | null;
}

interface QuickDispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drones: DispatchDroneOption[];
  /**
   * The fleet registry fetch failed. Distinguishes "you have no aircraft" from
   * "we could not load your aircraft" - which, now that the list is mostly built
   * FROM that registry, is the difference between an operator standing down and
   * an operator hitting reload. Silence here would report an outage as an empty
   * fleet at the moment someone is dispatching to an incident.
   */
  fleetUnavailable?: boolean;
  /**
   * The fleet registry fetch is still in flight.
   *
   * Without it the picker states "No drones available to dispatch" during the
   * first seconds of a cold COP load - a definitive sentence making exactly the
   * false claim this dialog was changed to stop making, at the moment an operator
   * is clicking a trigger.
   */
  fleetLoading?: boolean;
  /** Map pick, "dispatch here", or a trigger point - updates lat/lng fields when set */
  injectedCoordinates?: { latitude: number; longitude: number } | null;
  /**
   * Pre-fills description alongside injectedCoordinates, so a trigger dispatch
   * never stops on "enter a short description" - null for a plain map pick,
   * which has no source to draw a description from and keeps asking.
   */
  injectedDescription?: string | null;
  onPickTargetChange?: (active: boolean) => void;
  pickTargetActive?: boolean;
  /** The org's presets, ceiling and default. Seeds the altitude the operator sees. */
  altitudeProfile: AltitudeProfile;
  /** Who owns the ceiling, e.g. "FAA Part 107". Null if unknown. */
  ceilingAuthority: string | null;
  /**
   * A dispatch mission was created and commanded (HTTP 201). Same contract as
   * the dock launch overlay's `onLaunched`: the COP pins the aircraft's details
   * panel to THIS mission instead of inferring one from the drone id.
   */
  onDispatched?: (droneId: string, missionId: string) => void;
}

/** Mirrors `dispatchSchema.altitude_m` in src/api/missions.ts - which is itself
 *  `waypointItemSchema`'s bound, because a dispatch persists its route through
 *  `mission_waypoints` and must stay re-savable. */
const DISPATCH_ALT_MIN_M = 10;
const DISPATCH_ALT_MAX_M = 500;

export interface FormState {
  droneId: string;
  lat: string;
  lng: string;
  /** Metres. AltitudeControl owns its own draft-string handling internally. */
  altitudeM: number;
  description: string;
  submitting: boolean;
  prevOpen: boolean;
  prevInjLat: number | null;
  prevInjLng: number | null;
}

export type FormAction =
  | { type: 'field'; key: 'droneId' | 'lat' | 'lng' | 'description'; value: string }
  // Its own variant rather than widening `field`, because altitude is a number
  // and `field` carries a string.
  | { type: 'altitude'; meters: number }
  | { type: 'syncProps'; open: boolean; injLat: number | null; injLng: number | null; injDescription: string | null }
  | { type: 'submitting'; value: boolean }
  | { type: 'reset' };

/** Exported for test: the syncProps reconciliation is where a stale target's
 *  description used to leak onto the next unrelated dispatch. */
export function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'field':
      return { ...state, [action.key]: action.value };
    case 'altitude':
      return { ...state, altitudeM: action.meters };
    case 'syncProps': {
      const { open, injLat, injLng, injDescription } = action;
      const injChanged = state.prevInjLat !== injLat || state.prevInjLng !== injLng;
      const openChanged = state.prevOpen !== open;
      let { lat, lng, description } = state;
      if (injChanged && injLat != null && injLng != null) {
        // Injected coordinates (map pick / "dispatch here") populate the fields.
        lat = injLat.toFixed(6);
        lng = injLng.toFixed(6);
        // A NEW target REPLACES the description outright, including with the
        // empty string. Only overwriting when a description arrives leaks the
        // last trigger's text onto an unrelated map-pick dispatch: cancelling a
        // trigger spends `injectedDescription` in the reducer, but `description`
        // here is form state and is only cleared by `reset`, which runs on a
        // SUCCESSFUL submit and nowhere else. The operator never looks at this
        // field - that is the point of pre-filling it - so they press Deploy and
        // an unrelated sortie is recorded in the audit trail as a person-
        // detection response.
        description = injDescription ?? '';
      } else if ((openChanged || injChanged) && open && injLat == null) {
        // Dialog opened (or coordinates removed) without an injected target - clear.
        lat = '';
        lng = '';
        description = '';
      }
      return { ...state, lat, lng, description, prevOpen: open, prevInjLat: injLat, prevInjLng: injLng };
    }
    case 'submitting':
      return { ...state, submitting: action.value };
    case 'reset':
      // `altitudeM` deliberately survives: an operator answering two triggers in
      // a row wants the same altitude, and re-defaulting it silently under them
      // is how a second sortie flies at a height nobody chose.
      return { ...state, droneId: '', lat: '', lng: '', description: '' };
  }
}

export function QuickDispatchDialog({
  open,
  onOpenChange,
  drones,
  fleetUnavailable,
  fleetLoading,
  injectedCoordinates,
  injectedDescription,
  onPickTargetChange,
  pickTargetActive,
  altitudeProfile,
  ceilingAuthority,
  onDispatched,
}: QuickDispatchDialogProps) {
  const injLat = injectedCoordinates?.latitude ?? null;
  const injLng = injectedCoordinates?.longitude ?? null;
  const injDescription = injectedDescription ?? null;

  const [unit, setUnit] = useState<AltitudeUnit>(altitudeProfile.unit);

  // The org profile can land AFTER this dialog mounts - it is mounted for the
  // whole COP session, while `useAltitudeProfile` starts on the PLATFORM
  // fallback (76.2 m / ft) and only then fetches. Without this the operator is
  // pre-filled with a default their org never chose, every dispatch, all
  // session - and on an org whose ceiling is below the fallback, the pre-filled
  // value is already over it. Same fix, same touched-guard, as
  // DockLaunchOverlay.tsx:313-321.
  const altitudeTouchedRef = useRef(false);
  const seededDefaultRef = useRef(altitudeProfile.default_m);

  const [form, dispatch] = useReducer(formReducer, undefined, () => ({
    droneId: '',
    lat: injLat != null ? injLat.toFixed(6) : '',
    lng: injLng != null ? injLng.toFixed(6) : '',
    altitudeM: altitudeProfile.default_m,
    description: injDescription ?? '',
    submitting: false,
    prevOpen: open,
    prevInjLat: injLat,
    prevInjLng: injLng,
  }));

  useEffect(() => {
    if (altitudeTouchedRef.current) return;
    if (seededDefaultRef.current === altitudeProfile.default_m) return;
    seededDefaultRef.current = altitudeProfile.default_m;
    dispatch({ type: 'altitude', meters: altitudeProfile.default_m });
    setUnit(altitudeProfile.unit);
  }, [altitudeProfile.default_m, altitudeProfile.unit]);

  // Seed/clear the coordinate fields when the relevant props change, using a
  // render-time prev-prop comparison (resolved inside the reducer) instead of
  // effects - no extra commit and no stale UI between renders.
  if (form.prevOpen !== open || form.prevInjLat !== injLat || form.prevInjLng !== injLng) {
    dispatch({ type: 'syncProps', open, injLat, injLng, injDescription });
  }

  const { droneId, lat, lng, altitudeM, description, submitting } = form;

  /**
   * The dock the SELECTED aircraft will leave through, or null for a re-task.
   *
   * This is the highest-consequence fact on the screen and it used to be nowhere
   * on it: picking a parked aircraft opens a bay and powers on an engine, picking
   * an airborne one silently re-points a sortie already underway. One green
   * "Deploy" for both, over a description that says "from the drone's current
   * position", asks an operator to hold that difference in their head. The
   * product's bar is that they read it in under three seconds.
   */
  /** Stable id so the select can point `aria-describedby` at the fleet notice. */
  const fleetNoticeId = 'dispatch-fleet-notice';

  const selectedDock = drones.find((d) => d.id === droneId)?.dock ?? null;
  /** "dock M-011", or a bare "dock" while the registry is still loading. Never a
   *  placeholder name - see `DispatchDroneOption.dock`. */
  const dockPhrase = selectedDock ? (selectedDock.name ? `dock ${selectedDock.name}` : 'dock') : null;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      onPickTargetChange?.(false);
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    if (!droneId || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      toast.error('Select a drone and valid coordinates');
      return;
    }
    if (!description.trim()) {
      toast.error('Enter a short description');
      return;
    }
    dispatch({ type: 'submitting', value: true });
    try {
      const res = await api.post<{
        data: { id: string; name: string; assigned_drone_id: string | null; advisories?: unknown };
      }>('/api/missions/dispatch', {
        drone_id: droneId,
        latitude,
        longitude,
        altitude_m: altitudeM,
        description: description.trim(),
      }, {
        // The same ceiling the other two launch doors use, for the same reason
        // `launch-request.ts` states: doors that disagree about how long a launch
        // may take report different outcomes for the same sortie. Unbounded was
        // survivable while this door only ever re-tasked an AIRBORNE aircraft and
        // answered in milliseconds. Now that it can launch from a bay it holds the
        // connection through the whole sequence, past nginx's 60s default, and
        // without this an operator gets a bare "Request failed" for a drone that
        // is at that moment climbing out of the dock.
        timeoutMs: LAUNCH_REQUEST_TIMEOUT_MS,
      });
      const { id, name } = res.data;
      // The server's own idea of which aircraft it assigned, not ours. They
      // agree today; if they ever diverge the mission row is the authority.
      onDispatched?.(res.data.assigned_drone_id ?? droneId, id);
      // THE door the severity split was made for. `mission-deconflict` says so in as many words:
      // treating a same-aircraft SCHEDULED clash as a hard refusal "made an emergency quick
      // dispatch impossible for any airframe with a plan anywhere in the next couple of hours".
      // This is also the flow where an operator grabs the nearest free airframe under time
      // pressure — the likeliest one to be somebody else's planned sortie. Leaving it the one
      // silent door would have put the silence exactly where the change was aimed.
      const advisory = launchAdvisoryNote(res.data.advisories);
      const open = {
        action: {
          label: 'Open mission',
          onClick: () => {
            window.location.href = `/missions/${id}`;
          },
        },
      };
      if (advisory) {
        // Warning, not success: the description would otherwise print in the green this product
        // reserves for "nothing to see".
        toast.warning('Dispatch mission created', { ...open, description: `${name} · ${advisory}`, duration: 10_000 });
      } else {
        toast.success('Dispatch mission created', { ...open, description: name });
      }
      handleOpenChange(false);
      dispatch({ type: 'reset' });
    } catch (e) {
      // A timeout is NOT a refusal. Aborting the fetch does not cancel the launch
      // the server is still running, so "Dispatch failed" is a claim this client
      // cannot make - and on the docked path it is the likeliest thing to be wrong
      // about, because the bay sequence is what pushes the request past the proxy.
      // Same INDETERMINATE doctrine, same words, as the other two launch doors.
      if (isLaunchRequestTimeout(e)) {
        toast.error(LAUNCH_TIMEOUT_MESSAGE, { duration: 15_000 });
        return;
      }
      const msg = e instanceof ApiRequestError ? e.message : 'Dispatch failed';
      // The bay refusal has an escape hatch, and it is on the OTHER door. The dock
      // panel offers "I can see the bay is open, proceed" precisely because a board
      // that never reports tray position (M-001) refuses here forever. Without this
      // pointer, the same aircraft out of the same dock succeeds from the sidebar
      // and dead-ends here, with nothing saying why.
      const bayRefusal = e instanceof ApiRequestError && e.code === 'bay_not_open';
      toast.error(msg, bayRefusal
        ? { description: 'Confirm the bay from the dock panel to launch this drone.', duration: 15_000 }
        : undefined);
    } finally {
      dispatch({ type: 'submitting', value: false });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md text-popover-foreground"
        // Held shut while a launch is in flight. Closing does NOT abort the
        // request - the sortie still flies - but it clears the trigger this
        // dispatch is answering, so `markDispatched` never runs and the person
        // detection stays red on every operator's COP, inviting a second drone
        // at the same person. That window was about a second while this door only
        // re-tasked airborne drones; a docked launch holds it open for the whole
        // bay sequence, which is long enough to read as hung and be dismissed.
        //
        // FOUR exits, not three. `DialogContent` defaults to rendering an X that
        // is a `DialogPrimitive.Close` - it calls `onOpenChange(false)` directly
        // and reaches none of the three handlers below. With Cancel greyed out it
        // was the only live exit on the screen, i.e. the one an operator actually
        // clicks when a 50s bay sequence reads as hung.
        showCloseButton={!submitting}
        onEscapeKeyDown={(e) => { if (submitting) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (submitting) e.preventDefault(); }}
        onInteractOutside={(e) => { if (submitting) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="text-popover-foreground">Quick Dispatch</DialogTitle>
          <p className="text-xs text-muted-foreground">
            {dockPhrase
              ? `Launches from ${dockPhrase}: the bay opens and the drone powers on, then flies to the target.`
              : 'Create an in-progress mission from the drone’s current position to the target location.'}
          </p>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-muted-foreground" htmlFor="dispatch-drone">Drone</Label>
            {/* Frozen once a launch is in flight, with every other control below:
                `dockPhrase` is derived from the CURRENT selection, so changing it
                mid-sequence reflowed the header and the button to describe a
                different drone than the one a bay is physically opening for. */}
            <Select
              value={droneId}
              disabled={submitting}
              onValueChange={(value) => dispatch({ type: 'field', key: 'droneId', value })}
            >
              <SelectTrigger id="dispatch-drone" aria-describedby={fleetNoticeId}>
                <SelectValue placeholder="Select drone" />
              </SelectTrigger>
              <SelectContent>
                {drones.length === 0 ? (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    {fleetLoading
                      ? 'Loading fleet…'
                      : fleetUnavailable
                        ? 'Fleet list unavailable'
                        : 'No drones available to send'}
                  </div>
                ) : (
                  drones.map((d) => (
                    // `textValue` explicitly: Radix otherwise derives typeahead and
                    // the hidden native option from `textContent`, which for a
                    // docked row runs the two children together as "DJI-2dock
                    // M-011". The callsign is what an operator types.
                    //
                    // "dock M-011", not "in dock M-011": this is `drones.dock_id`,
                    // an ASSIGNMENT. Whether the drone is physically on the pad is
                    // the board's `drone_on_hub` byte, which is a different signal
                    // and known to disagree - the dock panel could say "Not in the
                    // dock" for the same drone this row claimed was in it.
                    <SelectItem key={d.id} value={d.id} textValue={d.label}>
                      {d.label}
                      {d.dock && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {d.dock.name ? `dock ${d.dock.name}` : 'dock'}
                        </span>
                      )}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {/* OUTSIDE the dropdown, and outside the empty branch, on purpose.
                Radix only mounts SelectContent while the menu is open, so a
                message living in there is one the operator has to go looking for.
                And gating the failure on an empty list hid it in the dangerous
                case: a registry failure with one drone still airborne renders
                a perfectly normal one-item picker, with no hint that every docked
                drone in the fleet is missing from it. */}
            {/* `role="status"` and wired to the trigger's `aria-describedby`: the
                notice resolves asynchronously, so without it a screen-reader
                operator hears a one-item list and is never told the rest of the
                fleet is missing from it - the same partial-list case, unsolved for
                the people who cannot see the amber line. */}
            <p id={fleetNoticeId} role="status" className="empty:hidden">
              {fleetUnavailable ? (
                <span className={`text-[11px] ${TONE_TEXT.amber}`}>
                  Fleet list unavailable - docked drones may be missing from this list. Reload to try again.
                </span>
              ) : fleetLoading ? (
                <span className="text-[11px] text-muted-foreground">Loading fleet…</span>
              ) : drones.length === 0 ? (
                // The genuinely-empty case, which lived ONLY inside the dropdown.
                // An operator who never opens it pressed Deploy and got "Select a
                // drone", which reads as "you forgot to pick one" rather than
                // "there is nothing to pick".
                <span className="text-[11px] text-muted-foreground">No drones available to send</span>
              ) : null}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Latitude</Label>
              <Input
                className="text-sm font-mono tabular-nums"
                value={lat}
                disabled={submitting}
                onChange={(e) => dispatch({ type: 'field', key: 'lat', value: e.target.value })}
                placeholder="34.052235"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-muted-foreground">Longitude</Label>
              <Input
                className="text-sm font-mono tabular-nums"
                value={lng}
                disabled={submitting}
                onChange={(e) => dispatch({ type: 'field', key: 'lng', value: e.target.value })}
                placeholder="-118.243683"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <AltitudeControl
              disabled={submitting}
              profile={altitudeProfile}
              authority={ceilingAuthority}
              valueMeters={altitudeM}
              onChange={(meters) => {
                altitudeTouchedRef.current = true;
                // AltitudeControl's own band bottoms out at the platform hard
                // floor (2 m) and, under a waiver, opens to 600 m - both
                // OUTSIDE what `dispatchSchema` accepts (10..500), because
                // these points persist through `mission_waypoints`. Dragging to
                // either stop would otherwise return a bare "Validation failed"
                // toast. Clamped here so the control can never offer a value
                // this door refuses.
                dispatch({ type: 'altitude', meters: Math.min(DISPATCH_ALT_MAX_M, Math.max(DISPATCH_ALT_MIN_M, meters)) });
              }}
              unit={unit}
              onUnitChange={setUnit}
              // A dispatch flies to a point and holds. It sets no return
              // altitude, exactly like the go-to bar and the command bar.
              showRth={false}
              // The datum is the aircraft's own launch point, not a dock pad -
              // a quick dispatch flies from wherever the airframe currently is.
              reference="AGL from launch point"
              label="Dispatch altitude"
            />
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full text-muted-foreground hover:text-foreground"
            disabled={submitting}
            onClick={() => onPickTargetChange?.(!pickTargetActive)}
          >
            <Crosshair className="size-4 mr-2" strokeWidth={1.5} />
            {pickTargetActive ? 'Cancel map pick' : 'Pick target on map'}
          </Button>
          {pickTargetActive && (
            <p className="text-[10px] text-status-amber-fg">Click the map to set target coordinates</p>
          )}

          <div className="space-y-1.5">
            <Label className="text-muted-foreground">Description</Label>
            <Textarea
              className="min-h-[72px] text-sm"
              value={description}
              disabled={submitting}
              onChange={(e) => dispatch({ type: 'field', key: 'description', value: e.target.value })}
              placeholder="Incident details, contact on scene, hazards…"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground"
            disabled={submitting}
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" className="bg-button-primary hover:bg-button-primary-hover text-primary-foreground" onClick={handleSubmit} disabled={submitting}>
            {submitting
              // The docked wait is up to ~50s of an unchanging button. Naming the
              // bay is what tells the operator this is the sequence running, not a
              // hung request they should dismiss (see the DialogContent guards).
              ? (dockPhrase ? 'Opening bay…' : 'Dispatching…')
              : (dockPhrase ? `Launch from ${dockPhrase}` : 'Deploy')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
